import type { SortingState } from "@tanstack/react-table";
import type { SortDescriptor } from "react-stately";

import { useCallback, useMemo } from "react";

interface UseTableSortingProps {
  defaultColumn: string;
  defaultDirection: "ascending" | "descending";
  setSortDescriptor: (sortDescriptor: SortDescriptor) => void;
  sortDescriptor: SortDescriptor;
}

interface UseTableSortingReturn {
  handleSort: (sorting: SortingState) => void;
  isDefaultSort: boolean;
  sorting: SortingState;
}

export function useTableSorting({
  defaultColumn,
  defaultDirection,
  setSortDescriptor,
  sortDescriptor,
}: UseTableSortingProps): UseTableSortingReturn {
  const isDefaultSort = useMemo(
    () =>
      sortDescriptor.column === defaultColumn &&
      sortDescriptor.direction === defaultDirection,
    [sortDescriptor, defaultColumn, defaultDirection],
  );

  const handleSort = useCallback(
    (sorting: SortingState) => {
      if (sorting.length === 0) {
        setSortDescriptor({
          column: defaultColumn,
          direction: defaultDirection,
        });

        return;
      }

      const firstSort = sorting[0];

      if (!firstSort) return;

      const currentColumn = firstSort.id;

      if (currentColumn === defaultColumn) {
        if (sortDescriptor.column !== defaultColumn) {
          setSortDescriptor({
            column: defaultColumn,
            direction: firstSort.desc ? "descending" : "ascending",
          });

          return;
        }

        const newDirection =
          sortDescriptor.direction === "ascending" ? "descending" : "ascending";

        setSortDescriptor({
          column: defaultColumn,
          direction: newDirection,
        });

        return;
      }

      if (sortDescriptor.column === currentColumn) {
        if (sortDescriptor.direction === "ascending") {
          setSortDescriptor({
            column: currentColumn,
            direction: "descending",
          });
        } else {
          setSortDescriptor({
            column: defaultColumn,
            direction: defaultDirection,
          });
        }
      } else {
        setSortDescriptor({
          column: currentColumn,
          direction: "ascending",
        });
      }
    },
    [defaultColumn, defaultDirection, setSortDescriptor, sortDescriptor],
  );

  const sorting = useMemo(
    (): SortingState => [
      {
        desc: sortDescriptor.direction === "descending",
        id: String(sortDescriptor.column),
      },
    ],
    [sortDescriptor],
  );

  return {
    handleSort,
    isDefaultSort,
    sorting,
  };
}
